import type { Routine, Workout } from '../types';

// Helper to check if an ID is a demo ID
export const isDemoId = (id?: string | null): boolean => {
  if (typeof id !== 'string') return false;
  return id.startsWith('demo-');
};

// Generate dates for the last 4 weeks
const getDateOffset = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split('T')[0];
};

export const MOCK_ROUTINES: Routine[] = [
  {
    id: 'demo-routine-push',
    name: 'Push Day',
    exercises: [
      { exercise_id: 'demo-ex-bench', name: 'Bench Press', sets: 4, reps: 8 },
      { exercise_id: 'demo-ex-ohp', name: 'Overhead Press', sets: 3, reps: 10 },
      { exercise_id: 'demo-ex-incline', name: 'Incline DB Press', sets: 3, reps: 12 },
      { exercise_id: 'demo-ex-tricep', name: 'Tricep Pushdown', sets: 3, reps: 15 },
    ],
  },
  {
    id: 'demo-routine-pull',
    name: 'Pull Day',
    exercises: [
      { exercise_id: 'demo-ex-deadlift', name: 'Deadlift', sets: 4, reps: 6 },
      { exercise_id: 'demo-ex-row', name: 'Barbell Row', sets: 4, reps: 8 },
      { exercise_id: 'demo-ex-pullup', name: 'Pull Ups', sets: 3, reps: 10 },
      { exercise_id: 'demo-ex-bicep', name: 'Bicep Curl', sets: 3, reps: 12 },
    ],
  },
  {
    id: 'demo-routine-legs',
    name: 'Leg Day',
    exercises: [
      { exercise_id: 'demo-ex-squat', name: 'Squat', sets: 4, reps: 8 },
      { exercise_id: 'demo-ex-rdl', name: 'Romanian Deadlift', sets: 3, reps: 10 },
      { exercise_id: 'demo-ex-legpress', name: 'Leg Press', sets: 3, reps: 12 },
      { exercise_id: 'demo-ex-calf', name: 'Calf Raises', sets: 4, reps: 15 },
    ],
  },
];

// Create workouts with progressive weights over 4 weeks
export const MOCK_WORKOUTS: Workout[] = [
  // Week 4 (oldest - 25-27 days ago)
  {
    id: 'demo-workout-1',
    routine_id: 'demo-routine-push',
    routine_name: 'Push Day',
    date: getDateOffset(27),
    exercises: [
      { exercise_id: 'demo-ex-bench', name: 'Bench Press', sets: [{ weight: 60, reps: 8 }, { weight: 60, reps: 8 }, { weight: 60, reps: 7 }, { weight: 60, reps: 6 }] },
      { exercise_id: 'demo-ex-ohp', name: 'Overhead Press', sets: [{ weight: 30, reps: 10 }, { weight: 30, reps: 10 }, { weight: 30, reps: 9 }] },
      { exercise_id: 'demo-ex-incline', name: 'Incline DB Press', sets: [{ weight: 16, reps: 12 }, { weight: 16, reps: 12 }, { weight: 16, reps: 10 }] },
      { exercise_id: 'demo-ex-tricep', name: 'Tricep Pushdown', sets: [{ weight: 20, reps: 15 }, { weight: 20, reps: 15 }, { weight: 20, reps: 14 }] },
    ],
  },
  {
    id: 'demo-workout-2',
    routine_id: 'demo-routine-pull',
    routine_name: 'Pull Day',
    date: getDateOffset(25),
    exercises: [
      { exercise_id: 'demo-ex-deadlift', name: 'Deadlift', sets: [{ weight: 80, reps: 6 }, { weight: 80, reps: 6 }, { weight: 80, reps: 5 }, { weight: 80, reps: 5 }] },
      { exercise_id: 'demo-ex-row', name: 'Barbell Row', sets: [{ weight: 50, reps: 8 }, { weight: 50, reps: 8 }, { weight: 50, reps: 7 }, { weight: 50, reps: 7 }] },
      { exercise_id: 'demo-ex-pullup', name: 'Pull Ups', sets: [{ weight: 0, reps: 10 }, { weight: 0, reps: 8 }, { weight: 0, reps: 7 }] },
      { exercise_id: 'demo-ex-bicep', name: 'Bicep Curl', sets: [{ weight: 10, reps: 12 }, { weight: 10, reps: 12 }, { weight: 10, reps: 11 }] },
    ],
  },
  {
    id: 'demo-workout-3',
    routine_id: 'demo-routine-legs',
    routine_name: 'Leg Day',
    date: getDateOffset(23),
    exercises: [
      { exercise_id: 'demo-ex-squat', name: 'Squat', sets: [{ weight: 70, reps: 8 }, { weight: 70, reps: 8 }, { weight: 70, reps: 7 }, { weight: 70, reps: 6 }] },
      { exercise_id: 'demo-ex-rdl', name: 'Romanian Deadlift', sets: [{ weight: 50, reps: 10 }, { weight: 50, reps: 10 }, { weight: 50, reps: 9 }] },
      { exercise_id: 'demo-ex-legpress', name: 'Leg Press', sets: [{ weight: 100, reps: 12 }, { weight: 100, reps: 12 }, { weight: 100, reps: 11 }] },
      { exercise_id: 'demo-ex-calf', name: 'Calf Raises', sets: [{ weight: 40, reps: 15 }, { weight: 40, reps: 15 }, { weight: 40, reps: 15 }, { weight: 40, reps: 14 }] },
    ],
  },

  // Week 3 (18-20 days ago)
  {
    id: 'demo-workout-4',
    routine_id: 'demo-routine-push',
    routine_name: 'Push Day',
    date: getDateOffset(20),
    exercises: [
      { exercise_id: 'demo-ex-bench', name: 'Bench Press', sets: [{ weight: 62.5, reps: 8 }, { weight: 62.5, reps: 8 }, { weight: 62.5, reps: 8 }, { weight: 62.5, reps: 7 }] },
      { exercise_id: 'demo-ex-ohp', name: 'Overhead Press', sets: [{ weight: 32.5, reps: 10 }, { weight: 32.5, reps: 10 }, { weight: 32.5, reps: 10 }] },
      { exercise_id: 'demo-ex-incline', name: 'Incline DB Press', sets: [{ weight: 18, reps: 12 }, { weight: 18, reps: 12 }, { weight: 18, reps: 11 }] },
      { exercise_id: 'demo-ex-tricep', name: 'Tricep Pushdown', sets: [{ weight: 22.5, reps: 15 }, { weight: 22.5, reps: 15 }, { weight: 22.5, reps: 15 }] },
    ],
  },
  {
    id: 'demo-workout-5',
    routine_id: 'demo-routine-pull',
    routine_name: 'Pull Day',
    date: getDateOffset(18),
    exercises: [
      { exercise_id: 'demo-ex-deadlift', name: 'Deadlift', sets: [{ weight: 85, reps: 6 }, { weight: 85, reps: 6 }, { weight: 85, reps: 6 }, { weight: 85, reps: 5 }] },
      { exercise_id: 'demo-ex-row', name: 'Barbell Row', sets: [{ weight: 52.5, reps: 8 }, { weight: 52.5, reps: 8 }, { weight: 52.5, reps: 8 }, { weight: 52.5, reps: 7 }] },
      { exercise_id: 'demo-ex-pullup', name: 'Pull Ups', sets: [{ weight: 0, reps: 10 }, { weight: 0, reps: 9 }, { weight: 0, reps: 8 }] },
      { exercise_id: 'demo-ex-bicep', name: 'Bicep Curl', sets: [{ weight: 12, reps: 12 }, { weight: 12, reps: 12 }, { weight: 12, reps: 12 }] },
    ],
  },
  {
    id: 'demo-workout-6',
    routine_id: 'demo-routine-legs',
    routine_name: 'Leg Day',
    date: getDateOffset(16),
    exercises: [
      { exercise_id: 'demo-ex-squat', name: 'Squat', sets: [{ weight: 75, reps: 8 }, { weight: 75, reps: 8 }, { weight: 75, reps: 8 }, { weight: 75, reps: 7 }] },
      { exercise_id: 'demo-ex-rdl', name: 'Romanian Deadlift', sets: [{ weight: 55, reps: 10 }, { weight: 55, reps: 10 }, { weight: 55, reps: 10 }] },
      { exercise_id: 'demo-ex-legpress', name: 'Leg Press', sets: [{ weight: 110, reps: 12 }, { weight: 110, reps: 12 }, { weight: 110, reps: 12 }] },
      { exercise_id: 'demo-ex-calf', name: 'Calf Raises', sets: [{ weight: 45, reps: 15 }, { weight: 45, reps: 15 }, { weight: 45, reps: 15 }, { weight: 45, reps: 15 }] },
    ],
  },

  // Week 2 (11-13 days ago)
  {
    id: 'demo-workout-7',
    routine_id: 'demo-routine-push',
    routine_name: 'Push Day',
    date: getDateOffset(13),
    exercises: [
      { exercise_id: 'demo-ex-bench', name: 'Bench Press', sets: [{ weight: 65, reps: 8 }, { weight: 65, reps: 8 }, { weight: 65, reps: 8 }, { weight: 65, reps: 8 }] },
      { exercise_id: 'demo-ex-ohp', name: 'Overhead Press', sets: [{ weight: 35, reps: 10 }, { weight: 35, reps: 10 }, { weight: 35, reps: 10 }] },
      { exercise_id: 'demo-ex-incline', name: 'Incline DB Press', sets: [{ weight: 20, reps: 12 }, { weight: 20, reps: 12 }, { weight: 20, reps: 12 }] },
      { exercise_id: 'demo-ex-tricep', name: 'Tricep Pushdown', sets: [{ weight: 25, reps: 15 }, { weight: 25, reps: 15 }, { weight: 25, reps: 15 }] },
    ],
  },
  {
    id: 'demo-workout-8',
    routine_id: 'demo-routine-pull',
    routine_name: 'Pull Day',
    date: getDateOffset(11),
    exercises: [
      { exercise_id: 'demo-ex-deadlift', name: 'Deadlift', sets: [{ weight: 90, reps: 6 }, { weight: 90, reps: 6 }, { weight: 90, reps: 6 }, { weight: 90, reps: 6 }] },
      { exercise_id: 'demo-ex-row', name: 'Barbell Row', sets: [{ weight: 55, reps: 8 }, { weight: 55, reps: 8 }, { weight: 55, reps: 8 }, { weight: 55, reps: 8 }] },
      { exercise_id: 'demo-ex-pullup', name: 'Pull Ups', sets: [{ weight: 0, reps: 10 }, { weight: 0, reps: 10 }, { weight: 0, reps: 9 }] },
      { exercise_id: 'demo-ex-bicep', name: 'Bicep Curl', sets: [{ weight: 12, reps: 12 }, { weight: 12, reps: 12 }, { weight: 12, reps: 12 }] },
    ],
  },
  {
    id: 'demo-workout-9',
    routine_id: 'demo-routine-legs',
    routine_name: 'Leg Day',
    date: getDateOffset(9),
    exercises: [
      { exercise_id: 'demo-ex-squat', name: 'Squat', sets: [{ weight: 77.5, reps: 8 }, { weight: 77.5, reps: 8 }, { weight: 77.5, reps: 8 }, { weight: 77.5, reps: 8 }] },
      { exercise_id: 'demo-ex-rdl', name: 'Romanian Deadlift', sets: [{ weight: 57.5, reps: 10 }, { weight: 57.5, reps: 10 }, { weight: 57.5, reps: 10 }] },
      { exercise_id: 'demo-ex-legpress', name: 'Leg Press', sets: [{ weight: 120, reps: 12 }, { weight: 120, reps: 12 }, { weight: 120, reps: 12 }] },
      { exercise_id: 'demo-ex-calf', name: 'Calf Raises', sets: [{ weight: 50, reps: 15 }, { weight: 50, reps: 15 }, { weight: 50, reps: 15 }, { weight: 50, reps: 15 }] },
    ],
  },

  // Week 1 (most recent - 4-6 days ago)
  {
    id: 'demo-workout-10',
    routine_id: 'demo-routine-push',
    routine_name: 'Push Day',
    date: getDateOffset(6),
    exercises: [
      { exercise_id: 'demo-ex-bench', name: 'Bench Press', sets: [{ weight: 67.5, reps: 8 }, { weight: 67.5, reps: 8 }, { weight: 67.5, reps: 8 }, { weight: 67.5, reps: 8 }] },
      { exercise_id: 'demo-ex-ohp', name: 'Overhead Press', sets: [{ weight: 37.5, reps: 10 }, { weight: 37.5, reps: 10 }, { weight: 37.5, reps: 10 }] },
      { exercise_id: 'demo-ex-incline', name: 'Incline DB Press', sets: [{ weight: 22, reps: 12 }, { weight: 22, reps: 12 }, { weight: 22, reps: 12 }] },
      { exercise_id: 'demo-ex-tricep', name: 'Tricep Pushdown', sets: [{ weight: 27.5, reps: 15 }, { weight: 27.5, reps: 15 }, { weight: 27.5, reps: 15 }] },
    ],
  },
  {
    id: 'demo-workout-11',
    routine_id: 'demo-routine-pull',
    routine_name: 'Pull Day',
    date: getDateOffset(4),
    exercises: [
      { exercise_id: 'demo-ex-deadlift', name: 'Deadlift', sets: [{ weight: 95, reps: 6 }, { weight: 95, reps: 6 }, { weight: 95, reps: 6 }, { weight: 95, reps: 6 }] },
      { exercise_id: 'demo-ex-row', name: 'Barbell Row', sets: [{ weight: 57.5, reps: 8 }, { weight: 57.5, reps: 8 }, { weight: 57.5, reps: 8 }, { weight: 57.5, reps: 8 }] },
      { exercise_id: 'demo-ex-pullup', name: 'Pull Ups', sets: [{ weight: 0, reps: 10 }, { weight: 0, reps: 10 }, { weight: 0, reps: 10 }] },
      { exercise_id: 'demo-ex-bicep', name: 'Bicep Curl', sets: [{ weight: 14, reps: 12 }, { weight: 14, reps: 12 }, { weight: 14, reps: 12 }] },
    ],
  },
  {
    id: 'demo-workout-12',
    routine_id: 'demo-routine-legs',
    routine_name: 'Leg Day',
    date: getDateOffset(2),
    exercises: [
      { exercise_id: 'demo-ex-squat', name: 'Squat', sets: [{ weight: 80, reps: 8 }, { weight: 80, reps: 8 }, { weight: 80, reps: 8 }, { weight: 80, reps: 8 }] },
      { exercise_id: 'demo-ex-rdl', name: 'Romanian Deadlift', sets: [{ weight: 60, reps: 10 }, { weight: 60, reps: 10 }, { weight: 60, reps: 10 }] },
      { exercise_id: 'demo-ex-legpress', name: 'Leg Press', sets: [{ weight: 130, reps: 12 }, { weight: 130, reps: 12 }, { weight: 130, reps: 12 }] },
      { exercise_id: 'demo-ex-calf', name: 'Calf Raises', sets: [{ weight: 55, reps: 15 }, { weight: 55, reps: 15 }, { weight: 55, reps: 15 }, { weight: 55, reps: 15 }] },
    ],
  },
];
